import { RECENT_PAGES_CAP, type RecentPageEntry } from "@njbsoft/store";

const STORAGE_KEY = "njbsoft-web-recent-pages";

/** The stored payload. The org stamp is what makes a seed safe: a sibling tab
 *  switching orgs writes the new org into the SHARED localStorage bag, so this
 *  tab can rehydrate into org B and find its own org-A trail in sessionStorage
 *  — org-A names over org-B links. */
export interface StoredRecentPages {
  orgId: string | null;
  entries: RecentPageEntry[];
}

/** The store surface this sync reads — structural so it stays decoupled from
 *  the app store's full middleware-wrapped type. */
interface SyncedState {
  recentPages: RecentPageEntry[];
  activeOrgId: string | null;
}

interface RecentPagesStoreApi {
  getState: () => SyncedState;
  setState: (partial: { recentPages: RecentPageEntry[] }) => void;
  subscribe: (listener: (state: SyncedState, previous: SyncedState) => void) => () => void;
}

/** A fresh value per call: callers own what they get back, so no reader can
 *  reach in and mutate a shared "nothing stored" constant. */
const empty = (): StoredRecentPages => ({ orgId: null, entries: [] });

function isRecentPageEntry(value: unknown): value is RecentPageEntry {
  if (typeof value !== "object" || value === null) return false;
  // Index-signature view of an unknown object so the field probes type-check;
  // every field is verified below, so nothing is trusted from the cast.
  const entry = value as Record<string, unknown>;
  return (
    typeof entry.path === "string" &&
    typeof entry.pathname === "string" &&
    typeof entry.label === "string" &&
    typeof entry.section === "string" &&
    typeof entry.visitedAt === "number"
  );
}

/**
 * Storage is best-effort, mirroring the sidebar-collapse helpers: anything
 * unreadable, unparseable, or off-shape yields an empty trail rather than an
 * error or a half-trusted entry list.
 */
export function readStoredRecentPages(): StoredRecentPages {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return empty();
    const parsed: unknown = JSON.parse(raw);
    if (typeof parsed !== "object" || parsed === null) return empty();
    // Index-signature view of an unknown object; both fields are verified below.
    const payload = parsed as Record<string, unknown>;
    const orgId = payload.orgId;
    if (typeof orgId !== "string" && orgId !== null) return empty();
    if (!Array.isArray(payload.entries)) return empty();
    const entries: RecentPageEntry[] = [];
    for (const item of payload.entries) {
      if (!isRecentPageEntry(item)) return empty();
      entries.push(item);
    }
    return { orgId, entries: entries.slice(0, RECENT_PAGES_CAP) };
  } catch {
    return empty();
  }
}

export function writeStoredRecentPages(orgId: string | null, entries: RecentPageEntry[]): void {
  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify({ orgId, entries }));
  } catch {
    // A browser refusing session storage degrades to a trail that does not
    // survive a reload rather than surfacing an error.
  }
}

/**
 * Gives the trail per-TAB durability: it survives a reload, and dies with the
 * tab. It lives here rather than in the shared slice so the slice stays
 * platform-neutral, and it rides sessionStorage rather than the store's
 * localStorage persist so a second tab keeps its own trail. The org-switch and
 * sign-out clears reach storage through the writer, which persists the emptied
 * array like any other change.
 *
 * Seeding reads the store's org DIRECTLY: the persist middleware rehydrates
 * synchronously inside `create`, so by the time this runs `activeOrgId` is the
 * org this tab is about to render — a trail stamped with any other org is left
 * on the floor.
 */
export function attachRecentPagesSessionSync(store: RecentPagesStoreApi): void {
  const stored = readStoredRecentPages();
  if (stored.entries.length > 0 && stored.orgId === store.getState().activeOrgId) {
    store.setState({ recentPages: stored.entries });
  }

  store.subscribe((state, previous) => {
    if (state.recentPages !== previous.recentPages) {
      writeStoredRecentPages(state.activeOrgId, state.recentPages);
    }
  });
}
